/*
 * Collection Tabs (template: collection.tabs)
 *
 * Main tabs switch between the "all", "problems" and "categories" views. Switching
 * views is client-side unless a filter is active, in which case the unfiltered
 * collection is reloaded. Inner options, pagination and "load more" fetch the
 * section through the Section Rendering API and swap it in place.
 *
 * Manual mode (data-mode="manual") is handled by ManualCollectionTabs below: all
 * products are rendered once and filtered / paginated in the browser.
 */
(() => {
  if (customElements.get('collection-tabs')) return;

  const ARROW_ICON =
    '<svg class="collection-tabs__arrow" width="8" height="14" viewBox="0 0 8 14" fill="none" aria-hidden="true"><path d="M1 1l6 6-6 6" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>';

  const SELECT_ARROW =
    '<svg class="collection-tabs__select-arrow" viewBox="0 0 12 8" fill="none" aria-hidden="true"><path d="M1 1.5l5 5 5-5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>';
  const ALL_VALUE = '__all__';

  /*
   * Manual mode's URL: one param per view, named after what it holds — ?problem=sleep rather
   * than ?tab=problems&option=sleep — so the tab is implied by the param. ?tab= on its own is
   * the menu screen with that tab's options open, and ?step=products is the "all products"
   * screen, which has no option of its own to name it. ("view" is Shopify's own param for
   * picking an alternate template, which this section's links already use, so it is not free.)
   */
  const VIEW_PARAM = { problems: 'problem', categories: 'category' };
  const PARAM_VIEW = { problem: 'problems', category: 'categories' };
  const STATE_PARAMS = ['tab', 'option', 'problem', 'category', 'step', 'p'];

  function optionLabel(option) {
    return [...option.querySelectorAll('.collection-tabs__option-part')]
      .map((part) => part.textContent.replace('|', '').trim())
      .join(' | ');
  }

  /* Mirrors each inner option list as a <select>; CSS shows it instead of the buttons on mobile. */
  function buildSelects(root, { valueOf, allowAll }) {
    // Only for the "Dropdown" style of the "Inner options on mobile" setting.
    if (!root.classList.contains('collection-tabs--mobile-options-dropdown')) return;

    root.querySelectorAll('[data-collection-tabs-options]').forEach((list) => {
      if (list.querySelector('.collection-tabs__select')) return;

      const select = document.createElement('select');
      select.className = 'collection-tabs__select';
      select.setAttribute('aria-label', 'בחירת סינון');

      if (allowAll) select.append(new Option('הכל', ALL_VALUE));
      list.querySelectorAll('.collection-tabs__option').forEach((option) => {
        select.append(new Option(optionLabel(option), valueOf(option)));
      });

      const wrap = document.createElement('div');
      wrap.className = 'collection-tabs__select-wrap';
      wrap.append(select);
      wrap.insertAdjacentHTML('beforeend', SELECT_ARROW);

      list.prepend(wrap);
      list.classList.add('collection-tabs__options--has-select');
    });
  }

  const MOBILE_QUERY = window.matchMedia('(max-width: 699px)');
  const SWIPER_GAP = 12;

  /*
   * "Steps" style of the "Inner options on mobile" setting (manual mode only): on mobile the
   * section works as two screens - the main tabs, where a tab opens its options as an accordion
   * below it, and then the products, with a back arrow on top, sliding forward / back. The nav,
   * option lists and results are moved into one wrapper per step; above mobile the wrappers are
   * display: contents, so the desktop layout is unchanged.
   */
  const STEP_ORDER = { menu: 0, products: 1 };
  const STEP_DURATION = 350;
  const REDUCED_MOTION = window.matchMedia('(prefers-reduced-motion: reduce)');

  /* The section renders the step screens; both modes just look them up and switch between them. */
  function stepElements(root) {
    const wrapper = root.querySelector('.collection-tabs__steps');
    if (!wrapper) return null;

    return {
      menu: wrapper.querySelector('[data-step="menu"]'),
      products: wrapper.querySelector('[data-step="products"]'),
    };
  }

  function setActiveStep(root, steps, step) {
    root.dataset.mobileStep = step;
    Object.entries(steps).forEach(([name, element]) => element?.classList.toggle('is-active', name === step));
  }

  function buildSteps(root) {
    const container = root.querySelector('[data-collection-tabs-container]');

    // The section renders the wrappers (and each tab's `order`) so the right screen is already
    // on screen at the first paint. Building them here would move the nav, the option lists and
    // the results after load, which reflows the whole section.
    const rendered = stepElements(container);
    if (rendered) return rendered;

    const nav = container.querySelector('.collection-tabs__nav');
    const optionLists = [...container.querySelectorAll('[data-collection-tabs-options]')];
    const results = container.querySelector('[data-collection-tabs-results]');

    const steps = document.createElement('div');
    steps.className = 'collection-tabs__steps';

    const makeStep = (name, withBack) => {
      const step = document.createElement('div');
      step.className = 'collection-tabs__step';
      step.dataset.step = name;
      if (withBack) {
        step.insertAdjacentHTML(
          'beforeend',
          `<button type="button" class="collection-tabs__back" data-collection-tabs-back>${ARROW_ICON}<span class="collection-tabs__back-label" data-collection-tabs-back-label></span></button>`
        );
      }
      steps.append(step);
      return step;
    };

    const menu = makeStep('menu', false);
    const products = makeStep('products', true);

    container.prepend(steps);
    menu.append(nav, ...optionLists);
    products.append(results);

    // Each tab's option list follows that tab in the column: on mobile the nav is
    // display: contents, so tabs and lists are items of the same flex column.
    let order = 0;
    nav.querySelectorAll('[data-collection-tabs-view]').forEach((tab) => {
      tab.style.order = order++;
      const list = menu.querySelector(`[data-collection-tabs-options="${tab.dataset.collectionTabsView}"]`);
      if (list) list.style.order = order++;
    });

    return { menu, products };
  }

  /*
   * "Scroll" style of the "Inner options on mobile" setting: on mobile, a visible option
   * row that overflows becomes a Swiper with auto-width slides. Rows that fit stay a plain
   * centered row, and everything is unwrapped again above mobile.
   */
  function destroyOptionsSwiper(list) {
    list.swiper.destroy(true, true);
    const wrapper = list.querySelector(':scope > .swiper-wrapper');
    [...wrapper.children].forEach((option) => {
      option.classList.remove('swiper-slide');
      list.append(option);
    });
    wrapper.remove();
    list.classList.remove('swiper');
  }

  function syncSwipers(root) {
    if (!root.classList.contains('collection-tabs--mobile-options-scroll') || typeof Swiper === 'undefined') return;

    root.querySelectorAll('[data-collection-tabs-options]').forEach((list) => {
      const shouldWatch = MOBILE_QUERY.matches && !list.hidden;

      if (list.swiper) {
        if (shouldWatch) {
          list.swiper.update();
        } else {
          destroyOptionsSwiper(list);
        }
        return;
      }

      if (!shouldWatch || list.scrollWidth <= list.clientWidth) return;

      const options = [...list.querySelectorAll(':scope > .collection-tabs__option')];
      const wrapper = document.createElement('div');
      wrapper.className = 'swiper-wrapper';
      options.forEach((option) => {
        option.classList.add('swiper-slide');
        wrapper.append(option);
      });
      list.append(wrapper);
      list.classList.add('swiper');

      const activeIndex = options.findIndex((option) => option.getAttribute('aria-current') === 'true');
      new Swiper(list, {
        slidesPerView: 'auto',
        spaceBetween: SWIPER_GAP,
        freeMode: true,
        initialSlide: Math.max(0, activeIndex),
      });
    });
  }

  function syncSelects(root) {
    root.querySelectorAll('.collection-tabs__select').forEach((select) => {
      const list = select.closest('[data-collection-tabs-options]');
      const options = [...list.querySelectorAll('.collection-tabs__option')];
      const activeIndex = options.findIndex((option) => option.getAttribute('aria-current') === 'true');
      const offset = select.options[0]?.value === ALL_VALUE ? 1 : 0;

      if (activeIndex === -1) {
        select.selectedIndex = offset ? 0 : -1;
      } else {
        select.selectedIndex = activeIndex + offset;
      }
    });
  }

  /*
   * Auto load on scroll: watches the last visible grid item (the start of the last row)
   * and calls onReach once its top edge is above the bottom of the viewport. It only
   * fires on entering, so scrolling up never loads, and it stops watching until the
   * caller observes again after the new items are in.
   */
  class InfiniteLoader {
    constructor(root, onReach) {
      this.root = root;
      this.observer = new IntersectionObserver((entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        this.observer.disconnect();
        onReach();
      });
    }

    observe() {
      this.observer.disconnect();
      const items = this.root.querySelectorAll('[data-collection-tabs-grid] > :not([hidden])');
      const lastItem = items[items.length - 1];
      if (lastItem) this.observer.observe(lastItem);
    }

    disconnect() {
      this.observer.disconnect();
    }
  }

  class ManualCollectionTabs {
    constructor(element) {
      this.element = element;
      this.container = element.querySelector('[data-collection-tabs-container]');
      this.items = [...element.querySelectorAll('[data-collection-tabs-grid] > *')];
      this.empty = element.querySelector('[data-collection-tabs-empty]');
      this.paginationSlot = element.querySelector('[data-collection-tabs-pagination-slot]');
      this.perPage = Number(this.container.dataset.perPage) || 24;
      this.paginationType = this.container.dataset.paginationType;
      this.autoSelect = this.container.dataset.autoSelect === 'true';
      this.appends = this.paginationType === 'load_more' || this.paginationType === 'infinite';
      this.infinite =
        this.paginationType === 'infinite'
          ? new InfiniteLoader(element, () => this.update({ ...this.state, page: this.state.page + 1 }, { push: false }))
          : null;
      this.stepEls = element.classList.contains('collection-tabs--mobile-options-steps') ? buildSteps(element) : null;
      this.step = 'menu';
      // Mobile "Steps": views whose options are open in the first screen.
      this.expandAll = this.container.dataset.expandAll === 'true';
      this.expanded = new Set();
    }

    get usesSteps() {
      return Boolean(this.stepEls) && MOBILE_QUERY.matches;
    }

    /*
     * Screen to open on load / history navigation: a named option shows that option's products
     * and ?step=products shows the "all products" screen, while a tab on its own is the menu,
     * with that tab's options open under it.
     */
    stepFromUrl() {
      const params = new URL(window.location.href).searchParams;
      if (params.get('step') === 'products') return 'products';

      // Only an option that actually exists opens its products; a stale or mistyped one
      // leaves the menu up, which is also what the pre-painted markup shows.
      const named = Object.entries(PARAM_VIEW).some(([param, view]) => this.findOption(view, params.get(param)));
      const tab = params.get('tab');
      return named || (tab && this.findOption(tab, params.get('option'))) ? 'products' : 'menu';
    }

    /*
     * Which tabs are open in the menu screen. With "Open all inner options by default" the
     * accordion is free-form and `expanded` tracks it; otherwise the open tab is simply the
     * current view, so there is no second copy of that state to drift from the URL — which is
     * what used to leave the wrong tab open after switching views on desktop.
     */
    syncExpanded() {
      if (!this.expandAll) return;

      this.element.querySelectorAll('[data-collection-tabs-options]').forEach((options) => {
        this.expanded.add(options.dataset.collectionTabsOptions);
      });
    }

    isExpanded(view) {
      return this.expandAll ? this.expanded.has(view) : this.state.view === view;
    }

    tabLabel(view) {
      return this.element.querySelector(`[data-collection-tabs-view="${view}"] .collection-tabs__tab-label`)?.textContent.trim() || '';
    }

    syncBackLabels() {
      if (!this.stepEls) return;
      const { view, key } = this.state;
      const option = key ? this.findOption(view, key) : null;

      this.stepEls.products.querySelector('[data-collection-tabs-back-label]').textContent =
        view === 'all' || !option ? this.tabLabel('all') : optionLabel(option);
    }

    /* Mobile "Steps": show each open tab's options under it; above mobile the lists follow the active view. */
    renderAccordion() {
      this.element.querySelectorAll('[data-collection-tabs-options]').forEach((options) => {
        const view = options.dataset.collectionTabsOptions;
        const tab = this.element.querySelector(`[data-collection-tabs-view="${view}"]`);

        if (!this.usesSteps) {
          tab?.removeAttribute('aria-expanded');
          return;
        }

        const open = this.isExpanded(view);
        options.hidden = !open;
        tab?.setAttribute('aria-expanded', open);
      });
    }

    goStep(step, { animate = true } = {}) {
      if (!this.stepEls) return;

      // Finish a slide still running, so rapid taps never leave two screens half-way.
      this.stepEls.menu.parentElement.getAnimations({ subtree: true }).forEach((animation) => animation.finish());

      const previous = this.step;
      this.step = step;
      this.element.dataset.mobileStep = step;
      this.syncBackLabels();

      const from = this.stepEls[previous];
      const to = this.stepEls[step];
      Object.values(this.stepEls).forEach((element) => element.classList.toggle('is-active', element === to));

      if (previous === step || !animate || !MOBILE_QUERY.matches) return;

      // Keep the top of the new screen in view (the products list may have been scrolled far down).
      const top = this.container.getBoundingClientRect().top;
      if (top < 0) window.scrollBy({ top: top - 20, behavior: 'instant' });

      if (REDUCED_MOTION.matches) return;

      // Forward slides the new screen in from the reading-end side (left in RTL), back from the start side.
      const forward = STEP_ORDER[step] > STEP_ORDER[previous];
      const rtl = getComputedStyle(this.element).direction === 'rtl';
      const sign = (forward ? 1 : -1) * (rtl ? -1 : 1);
      const offset = (direction) => `translateX(calc(${direction} * (100% + 48px)))`;
      const timing = { duration: STEP_DURATION, easing: 'cubic-bezier(0.4, 0, 0.2, 1)' };

      from.classList.add('is-leaving');
      to.animate([{ transform: offset(sign) }, { transform: 'translateX(0)' }], timing);
      from
        .animate([{ transform: 'translateX(0)' }, { transform: offset(-sign) }], timing)
        .finished.catch(() => {})
        .finally(() => from.classList.remove('is-leaving'));
    }

    /*
     * Back goes to the menu and drops the option, as a plain state change. It used to call
     * history.back() instead, counting the screens it had pushed — but tab and option clicks
     * on desktop push entries of their own without touching that count, so after a resize the
     * arrow walked back through those desktop entries rather than returning to the menu.
     */
    goBack() {
      this.goStep('menu');
      this.update({ ...this.state, key: null, page: 1 }, { push: false });
    }

    connect() {
      buildSelects(this.element, { valueOf: (option) => option.dataset.key, allowAll: !this.autoSelect });

      this.element.addEventListener('click', this.onClick);
      this.element.addEventListener('change', this.onChange);
      window.addEventListener('popstate', this.onPopState);
      this.state = this.readState();
      if (this.stepEls) this.syncExpanded();
      this.render();
      // The screen already shows this state (snippets/collection-tabs-initial-state.liquid), so
      // take over from the stylesheet that produced it rather than leaving it to override clicks.
      this.element.collectionTabsInitial?.style?.remove();

      if (this.stepEls) {
        MOBILE_QUERY.addEventListener('change', this.onBreakpoint);
        // Already the screen on display, so it must not slide in.
        this.goStep(this.stepFromUrl(), { animate: false });
      }
    }

    disconnect() {
      this.element.removeEventListener('click', this.onClick);
      this.element.removeEventListener('change', this.onChange);
      window.removeEventListener('popstate', this.onPopState);
      MOBILE_QUERY.removeEventListener('change', this.onBreakpoint);
      this.infinite?.disconnect();
    }

    /*
     * Crossing the mobile breakpoint swaps between the accordion and the plain desktop lists.
     * The screen is taken from the URL rather than from `this.step`: above the breakpoint there
     * are no screens to be on, so tab and option clicks there never move it, and replaying it on
     * the way back down would open the menu over an option that is in fact selected.
     */
    onBreakpoint = () => {
      this.render();
      this.goStep(this.stepFromUrl(), { animate: false });
    };

    onChange = (event) => {
      const select = event.target.closest('.collection-tabs__select');
      if (!select) return;

      const key = select.value === ALL_VALUE ? null : select.value;
      this.update({ ...this.state, key, page: 1 });
    };

    optionsFor(view) {
      return this.element.querySelector(`[data-collection-tabs-options="${view}"]`);
    }

    findOption(view, key) {
      const options = this.optionsFor(view)?.querySelectorAll('[data-collection-tabs-manual-option]') || [];
      return [...options].find((option) => option.dataset.key === key) || null;
    }

    firstOptionKey(view) {
      return this.optionsFor(view)?.querySelector('[data-collection-tabs-manual-option]')?.dataset.key || null;
    }

    /* See VIEW_PARAM; p is the page, since "page" belongs to Shopify pagination. */
    readState() {
      const params = new URL(window.location.href).searchParams;
      let view = 'all';
      let key = null;

      for (const [param, candidate] of Object.entries(PARAM_VIEW)) {
        const value = params.get(param);
        if (value && this.findOption(candidate, value)) {
          view = candidate;
          key = value;
          break;
        }
      }

      if (!key) {
        const tab = params.get('tab');
        if (tab && this.optionsFor(tab)) view = tab;

        // ?tab=&option= is the scheme this section used before; keep reading it so links
        // that are already out there still open what they say.
        const option = params.get('option');
        if (view !== 'all' && option && this.findOption(view, option)) key = option;
        if (view !== 'all' && !key && this.autoSelect) key = this.firstOptionKey(view);
      }

      const page = Math.max(1, parseInt(params.get('p'), 10) || 1);
      return { view, key, page };
    }

    writeState(push) {
      const url = new URL(window.location.href);
      STATE_PARAMS.forEach((name) => url.searchParams.delete(name));

      const { view, key, page } = this.state;
      if (key) url.searchParams.set(VIEW_PARAM[view], key);
      else if (view !== 'all') url.searchParams.set('tab', view);
      // "all" is otherwise the empty URL, which would reopen as the menu on the next load.
      else if (this.stepEls && this.step === 'products') url.searchParams.set('step', 'products');

      if (page > 1 && !this.appends) url.searchParams.set('p', page);

      window.history[push ? 'pushState' : 'replaceState']({}, '', url);
    }

    onClick = (event) => {
      if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

      if (this.usesSteps && this.onStepClick(event)) return;

      const tab = event.target.closest('[data-collection-tabs-view]');
      if (tab) {
        const view = tab.dataset.collectionTabsView;
        if (view === this.state.view) return;

        const key = view !== 'all' && this.autoSelect ? this.firstOptionKey(view) : null;
        this.update({ view, key, page: 1 });
        return;
      }

      const option = event.target.closest('[data-collection-tabs-manual-option]');
      if (option) {
        const isActive = option.dataset.key === this.state.key;
        if (isActive && this.autoSelect) return;

        this.update({ ...this.state, key: isActive ? null : option.dataset.key, page: 1 });
        return;
      }

      const page = event.target.closest('[data-collection-tabs-page]');
      if (page) {
        this.update({ ...this.state, page: Number(page.dataset.page) }, { scroll: true });
        return;
      }

      const loadMore = event.target.closest('[data-collection-tabs-load-more]');
      if (loadMore) {
        this.update({ ...this.state, page: this.state.page + 1 }, { push: false });
      }
    };

    /* Mobile "Steps": a tab opens its options below it (or all products), an option opens its products. */
    onStepClick(event) {
      if (event.target.closest('[data-collection-tabs-back]')) {
        this.goBack();
        return true;
      }

      const tab = event.target.closest('[data-collection-tabs-view]');
      if (tab) {
        const view = tab.dataset.collectionTabsView;

        if (this.optionsFor(view)) {
          // With every tab open by default there is no single open tab to name in the URL, so
          // the accordion is free-form; otherwise opening a tab is the view change the URL
          // already describes, exactly as it is on desktop.
          if (this.expandAll) {
            if (this.expanded.has(view)) this.expanded.delete(view);
            else this.expanded.add(view);
            this.renderAccordion();
            return true;
          }

          this.update({ view: this.state.view === view ? 'all' : view, key: null, page: 1 });
          return true;
        }

        this.goStep('products');
        this.update({ view, key: null, page: 1 });
        return true;
      }

      const option = event.target.closest('[data-collection-tabs-manual-option]');
      if (option) {
        const view = option.closest('[data-collection-tabs-options]').dataset.collectionTabsOptions;
        this.goStep('products');
        this.update({ view, key: option.dataset.key, page: 1 });
        return true;
      }

      return false;
    }

    onPopState = () => {
      this.state = this.readState();
      this.render();
      if (this.stepEls) this.goStep(this.stepFromUrl());
    };

    update(state, { push = true, scroll = false } = {}) {
      this.state = state;
      this.render();
      this.writeState(push);
      if (scroll) this.element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }

    render() {
      const { view, key } = this.state;
      const activeOptions = this.optionsFor(view);

      this.element.querySelectorAll('[data-collection-tabs-view]').forEach((tab) => {
        if (tab.dataset.collectionTabsView === view) {
          tab.setAttribute('aria-current', 'true');
        } else {
          tab.removeAttribute('aria-current');
        }
      });

      this.element.querySelectorAll('[data-collection-tabs-options]').forEach((options) => {
        options.hidden = options !== activeOptions;
      });

      if (this.stepEls) this.renderAccordion();

      this.element.querySelectorAll('[data-collection-tabs-manual-option]').forEach((option) => {
        if (option.dataset.key === key && option.closest('[data-collection-tabs-options]') === activeOptions) {
          option.setAttribute('aria-current', 'true');
        } else {
          option.removeAttribute('aria-current');
        }
      });

      const activeOption = key ? this.findOption(view, key) : null;
      const values = activeOption ? activeOption.dataset.values.split(' ') : null;
      const attribute = activeOptions?.dataset.attribute;

      const matches = this.items.filter((item) => {
        if (!values || !attribute) return true;
        const itemValues = item.dataset[attribute] || '';
        return values.some((value) => itemValues.includes(` ${value} `));
      });

      const pageCount = Math.max(1, Math.ceil(matches.length / this.perPage));
      this.state.page = Math.min(this.state.page, pageCount);

      const start = this.appends ? 0 : (this.state.page - 1) * this.perPage;
      const visible = new Set(matches.slice(start, this.state.page * this.perPage));

      this.items.forEach((item) => {
        item.hidden = !visible.has(item);
      });

      if (this.empty) this.empty.hidden = matches.length > 0;
      this.syncBackLabels();
      syncSelects(this.element);
      syncSwipers(this.element);
      this.renderPagination(pageCount);

      if (this.infinite) {
        if (this.state.page < pageCount) {
          this.infinite.observe();
        } else {
          this.infinite.disconnect();
        }
      }
    }

    renderPagination(pageCount) {
      if (!this.paginationSlot) return;

      const { page } = this.state;

      if (pageCount <= 1) {
        this.paginationSlot.innerHTML = '';
        return;
      }

      if (this.paginationType === 'infinite') {
        this.paginationSlot.innerHTML = '';
        return;
      }

      if (this.paginationType === 'load_more') {
        this.paginationSlot.innerHTML =
          page < pageCount
            ? '<div class="collection-tabs__load-more" data-collection-tabs-pagination><button type="button" class="collection-tabs__load-more-button" data-collection-tabs-load-more>טען עוד מוצרים</button></div>'
            : '';
        return;
      }

      const numbers = [];
      for (let number = 1; number <= pageCount; number++) {
        if (number === 1 || number === pageCount || Math.abs(number - page) <= 1) {
          numbers.push(number);
        } else if (numbers[numbers.length - 1] !== 'gap') {
          numbers.push('gap');
        }
      }

      const parts = [];
      if (page > 1) {
        parts.push(
          `<button type="button" class="collection-tabs__page collection-tabs__page--arrow" aria-label="העמוד הקודם" data-collection-tabs-page data-page="${page - 1}">${ARROW_ICON}</button>`
        );
      }

      numbers.forEach((number) => {
        if (number === 'gap') {
          parts.push('<span class="collection-tabs__page collection-tabs__page--gap">…</span>');
        } else if (number === page) {
          parts.push(`<span class="collection-tabs__page" aria-current="page">${number}</span>`);
        } else {
          parts.push(
            `<button type="button" class="collection-tabs__page" data-collection-tabs-page data-page="${number}">${number}</button>`
          );
        }
      });

      if (page < pageCount) {
        parts.push(
          `<button type="button" class="collection-tabs__page collection-tabs__page--arrow collection-tabs__page--next" aria-label="העמוד הבא" data-collection-tabs-page data-page="${page + 1}">${ARROW_ICON}</button>`
        );
      }

      this.paginationSlot.innerHTML = `<nav class="collection-tabs__pagination" aria-label="ניווט בין עמודים" data-collection-tabs-pagination>${parts.join('')}</nav>`;
    }
  }

  class CollectionTabs extends HTMLElement {
    #abortController = null;
    #manual = null;

    #desktopQuery = window.matchMedia('(min-width: 700px)');
    #resizeTimer = null;

    #onResize = () => {
      clearTimeout(this.#resizeTimer);
      this.#resizeTimer = setTimeout(() => syncSwipers(this), 150);
    };

    /* The transparent header is desktop only: the theme's CSS keys off this attribute. */
    #syncTransparentHeader = () => {
      this.toggleAttribute('allow-transparent-header', this.#desktopQuery.matches);
    };

    #infinite = new InfiniteLoader(this, () => {
      const trigger = this.querySelector('[data-collection-tabs-infinite] [data-collection-tabs-load-more]');
      if (trigger) this.#loadMore(trigger.href);
    });

    /* Watch the last row again whenever the grid or its "next page" link changes. */
    #syncInfinite() {
      if (this.querySelector('[data-collection-tabs-infinite]')) {
        this.#infinite.observe();
      } else {
        this.#infinite.disconnect();
      }
    }

    connectedCallback() {
      if (this.hasAttribute('data-transparent-header')) {
        this.#syncTransparentHeader();
        this.#desktopQuery.addEventListener('change', this.#syncTransparentHeader);
      }

      window.addEventListener('resize', this.#onResize);

      if (this.#container?.dataset.mode === 'manual') {
        this.#manual = new ManualCollectionTabs(this);
        this.#manual.connect();
        return;
      }

      this.addEventListener('click', this.#onClick);
      this.addEventListener('change', this.#onChange);
      window.addEventListener('popstate', this.#onPopState);
      MOBILE_QUERY.addEventListener('change', this.#onBreakpoint);
      this.#syncView();
      this.#syncInfinite();
    }

    disconnectedCallback() {
      this.#desktopQuery.removeEventListener('change', this.#syncTransparentHeader);
      window.removeEventListener('resize', this.#onResize);
      clearTimeout(this.#resizeTimer);
      this.#infinite.disconnect();

      if (this.#manual) {
        this.#manual.disconnect();
        this.#manual = null;
        return;
      }

      this.removeEventListener('click', this.#onClick);
      this.removeEventListener('change', this.#onChange);
      window.removeEventListener('popstate', this.#onPopState);
      MOBILE_QUERY.removeEventListener('change', this.#onBreakpoint);
    }

    #onBreakpoint = () => {
      this.#syncView();
    };

    get #container() {
      return this.querySelector('[data-collection-tabs-container]');
    }

    get #serverView() {
      return this.#container?.dataset.serverView || 'all';
    }

    /*
     * Mobile "Steps" in server mode. Every transition is a URL that #load() fetches through the
     * Section Rendering API, and the markup that comes back is already rendered for it, so there
     * is no step state to keep here: the screen follows from the active option and ?step=.
     * The container is replaced on each load, so the wrappers are looked up fresh.
     */
    get #steps() {
      return stepElements(this);
    }

    get #usesSteps() {
      return Boolean(this.#steps) && MOBILE_QUERY.matches;
    }

    get #activeOption() {
      return this.querySelector('[data-collection-tabs-option][aria-current="true"]');
    }

    #stepFromState() {
      if (new URL(window.location.href).searchParams.get('step') === 'products') return 'products';
      return this.#activeOption ? 'products' : 'menu';
    }

    #syncSteps(view) {
      const steps = this.#steps;
      if (!steps) return;

      setActiveStep(this, steps, this.#stepFromState());

      this.querySelectorAll('[data-collection-tabs-options]').forEach((list) => {
        const tab = this.querySelector(`[data-collection-tabs-view="${list.dataset.collectionTabsOptions}"]`);
        if (MOBILE_QUERY.matches) tab?.setAttribute('aria-expanded', list.dataset.collectionTabsOptions === view);
        else tab?.removeAttribute('aria-expanded');
      });

      const label = this.querySelector('[data-collection-tabs-back-label]');
      if (!label) return;

      const option = this.#activeOption;
      label.textContent = option
        ? optionLabel(option)
        : this.querySelector('[data-collection-tabs-view="all"] .collection-tabs__tab-label')?.textContent.trim() || '';
    }

    /* Back drops the filter and the step marker, keeping the tab, so the menu reopens on it. */
    #goBack() {
      const view = this.#resolveView();
      const url = new URL(window.location.href);
      [...url.searchParams.keys()].filter((name) => name.startsWith('filter.')).forEach((name) => url.searchParams.delete(name));
      url.searchParams.delete('step');
      if (view === 'all') url.searchParams.delete('tab');
      else url.searchParams.set('tab', view);

      if (this.#serverView !== 'all') {
        this.#load(url.toString());
        return;
      }

      window.history.pushState({}, '', url);
      this.#syncView(view);
    }

    /* The "all" tab has no options to open, so on mobile it goes straight to its products. */
    #showAllProducts(href) {
      const url = new URL(href, window.location.origin);
      url.searchParams.set('step', 'products');

      if (this.#serverView !== 'all') {
        this.#load(url.toString());
        return;
      }

      window.history.pushState({}, '', url);
      this.#syncView('all');
    }

    /* An active filter decides the view; otherwise fall back to the ?tab= param. */
    #resolveView() {
      if (this.#serverView !== 'all') return this.#serverView;

      const view = new URL(window.location.href).searchParams.get('tab');
      return view && this.querySelector(`[data-collection-tabs-options="${view}"]`) ? view : 'all';
    }

    #onChange = (event) => {
      const select = event.target.closest('.collection-tabs__select');
      if (!select) return;

      if (select.value !== ALL_VALUE) {
        this.#load(select.value);
        return;
      }

      // "All" clears the option: go back to the tab's unfiltered URL.
      const view = select.closest('[data-collection-tabs-options]').dataset.collectionTabsOptions;
      const tab = this.querySelector(`[data-collection-tabs-view="${view}"]`);
      if (tab) this.#load(tab.href);
    };

    #syncView(view = this.#resolveView()) {
      buildSelects(this, {
        valueOf: (option) => option.href,
        allowAll: this.#container?.dataset.autoSelect !== 'true',
      });

      this.querySelectorAll('[data-collection-tabs-view]').forEach((tab) => {
        if (tab.dataset.collectionTabsView === view) {
          tab.setAttribute('aria-current', 'true');
        } else {
          tab.removeAttribute('aria-current');
        }
      });

      this.querySelectorAll('[data-collection-tabs-options]').forEach((options) => {
        options.hidden = options.dataset.collectionTabsOptions !== view;
      });

      this.#syncSteps(view);
      syncSelects(this);
      syncSwipers(this);
    }

    #onClick = (event) => {
      if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
        return;
      }

      if (this.#usesSteps && event.target.closest('[data-collection-tabs-back]')) {
        event.preventDefault();
        this.#goBack();
        return;
      }

      const tab = event.target.closest('[data-collection-tabs-view]');
      if (tab) {
        event.preventDefault();
        const view = tab.dataset.collectionTabsView;

        if (this.#usesSteps && !this.querySelector(`[data-collection-tabs-options="${view}"]`)) {
          this.#showAllProducts(tab.href);
          return;
        }

        // Tapping the tab that is already open closes its accordion, as it does in manual mode.
        if (this.#usesSteps && this.#serverView === 'all' && view === this.#resolveView()) {
          const url = new URL(window.location.href);
          url.searchParams.delete('tab');
          url.searchParams.delete('step');
          window.history.pushState({}, '', url);
          this.#syncView('all');
          return;
        }

        this.#selectView(view, tab.href, tab.hasAttribute('data-collection-tabs-reload'));
        return;
      }

      const loadMore = event.target.closest('[data-collection-tabs-load-more]');
      if (loadMore) {
        event.preventDefault();
        this.#loadMore(loadMore.href);
        return;
      }

      const option = event.target.closest('[data-collection-tabs-option]');
      if (option) {
        event.preventDefault();
        if (option.getAttribute('aria-current') === 'true' && option.href === window.location.href) return;
        this.#load(option.href);
        return;
      }

      const page = event.target.closest('[data-collection-tabs-page]');
      if (page) {
        event.preventDefault();
        this.#load(page.href, { scroll: true });
      }
    };

    #onPopState = () => {
      this.#load(window.location.href, { push: false });
    };

    #selectView(view, href, reload = false) {
      // Already showing this view's filtered results. Without a filter there are none to be
      // showing, and the view comes from ?tab= instead — the check below is the one that
      // applies then. (Reading it from #serverView here used to swallow the "all" tab, since
      // that is also what #serverView says when nothing is filtered, leaving the open option
      // list on screen.)
      if (this.#serverView !== 'all' && view === this.#serverView) return;

      // The tab applies its first option, or a filter of another view must be cleared.
      if (reload || this.#serverView !== 'all') {
        this.#load(href);
        return;
      }

      if (view === this.#resolveView()) return;

      window.history.pushState({}, '', href);
      this.#syncView(view);
    }

    async #fetchSection(url) {
      this.#abortController?.abort();
      this.#abortController = new AbortController();

      const fetchUrl = new URL(url, window.location.origin);
      fetchUrl.searchParams.set('section_id', this.dataset.sectionId);

      const response = await fetch(fetchUrl, { signal: this.#abortController.signal });
      if (!response.ok) throw new Error(`Collection tabs: ${response.status}`);

      return new DOMParser().parseFromString(await response.text(), 'text/html');
    }

    async #load(href, { push = true, scroll = false } = {}) {
      // Links rendered by a section request can carry section_id; keep it out of the address bar.
      const cleanUrl = new URL(href, window.location.origin);
      cleanUrl.searchParams.delete('section_id');
      const url = cleanUrl.toString();

      this.classList.add('is-loading');

      try {
        const doc = await this.#fetchSection(url);
        const nextContainer = doc.querySelector('[data-collection-tabs-container]');
        if (!nextContainer) throw new Error('Collection tabs: container not found');

        this.#container.replaceWith(nextContainer);

        if (push) window.history.pushState({}, '', url);
        this.#syncView();
        this.#syncInfinite();

        if (scroll) this.scrollIntoView({ behavior: 'smooth', block: 'start' });
        this.classList.remove('is-loading');
      } catch (error) {
        if (error.name === 'AbortError') return;
        window.location.href = url;
      }
    }

    async #loadMore(url) {
      this.classList.add('is-loading');

      try {
        const doc = await this.#fetchSection(url);
        const grid = this.querySelector('[data-collection-tabs-grid]');
        const nextItems = doc.querySelectorAll('[data-collection-tabs-grid] > *');
        const pagination = this.querySelector('[data-collection-tabs-pagination]');
        const nextPagination = doc.querySelector('[data-collection-tabs-pagination]');

        grid?.append(...nextItems);

        if (nextPagination) {
          pagination?.replaceWith(nextPagination);
        } else {
          pagination?.remove();
        }

        this.classList.remove('is-loading');
        this.#syncInfinite();
      } catch (error) {
        if (error.name === 'AbortError') return;
        window.location.href = url;
      }
    }
  }

  customElements.define('collection-tabs', CollectionTabs);
})();
